class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  _onDocumentClick(event) {
    try {
      // If click is outside this cart-drawer element, close it
      if (!this.contains(event.target)) {
        this.close();
      }
    } catch (e) {
      // ignore
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // Execute any scripts within a container (both inline and external)
  runScripts(container) {
    if (!container) return;
    const scripts = Array.from(container.querySelectorAll("script"));
    scripts.forEach((old) => {
      const s = document.createElement("script");
      // Copy attributes (type, src, async, etc.)
      for (const attr of old.attributes) {
        s.setAttribute(attr.name, attr.value);
      }
      // Inline content
      if (old.textContent) s.textContent = old.textContent;
      // Replace
      old.parentNode.replaceChild(s, old);
    });
  }

  // Inject app blocks into the drawer CTAs
  injectAppBlocks({ preservedHtml, sectionHtml } = {}) {
    const drawerCtas =
      this.querySelector(".cart__ctas") ||
      document.querySelector("#CartDrawer .cart__ctas");
    if (!drawerCtas) return;

    // preservedHtml > server blocks from sectionHtml > hidden page copy
    if (preservedHtml) {
      drawerCtas.innerHTML = preservedHtml;
      drawerCtas.setAttribute("data-app-blocks-injected", "true");
      this.runScripts(drawerCtas);
      this.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          detail: { sectionId: "cart-drawer" },
        })
      );
      return;
    }

    // Try to extract from the refreshed section HTML
    if (sectionHtml) {
      try {
        const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
        const serverAppBlocks = doc.getElementById("CartDrawer-AppBlocks");
        if (serverAppBlocks) {
          drawerCtas.innerHTML = serverAppBlocks.innerHTML;
          drawerCtas.setAttribute("data-app-blocks-injected", "true");
          this.runScripts(drawerCtas);
          this.dispatchEvent(
            new CustomEvent("shopify:section:load", {
              detail: { sectionId: "cart-drawer" },
            })
          );
          return;
        }
      } catch (_) {}
    }

    // Fallback: copy from hidden container present on the page
    const pageAppBlocks = document.getElementById("CartDrawer-AppBlocks");
    if (pageAppBlocks) {
      drawerCtas.innerHTML = pageAppBlocks.innerHTML;
      drawerCtas.setAttribute("data-app-blocks-injected", "true");
      this.runScripts(drawerCtas);
      this.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          detail: { sectionId: "cart-drawer" },
        })
      );
    }
  }

  // Observe CTAs content and re-inject if something wipes it out
  startCtasObserver() {
    const ctas =
      this.querySelector(".cart__ctas") ||
      document.querySelector("#CartDrawer .cart__ctas");
    if (!ctas || this._ctasObserver) return;
    this._ctasObserver = new MutationObserver(() => {
      // If content gets replaced/cleared and our data marker is missing, re-inject
      const hasMarker =
        ctas.getAttribute("data-app-blocks-injected") === "true";
      if (!hasMarker) {
        this.injectAppBlocks();
      }
    });
    this._ctasObserver.observe(ctas, { childList: true, subtree: true });
  }

  open(triggeredBy, opts = {}) {
    // Open Exception
    // If bonus app is installed, we must wait to open the slider
    if (
      window.__bonus_config &&
      !opts.bypass &&
      !window.__BONUS_CART_COMPLETED
    ) {
      console.log("block active");
      //if (this.__waitingForBonus ?? false) return;
      //this.__waitingForBonus = true;

      const proceed = () => {
        window.__BONUS_CART_COMPLETED = true;
        //this.__waitingForBonus = false;
        this.fetchLatestSections()
          .catch(() => {
            /* ignore */
          })
          .finally(() => this.open(triggeredBy, { bypass: true }));
      };

      // Defined by the app
      document.addEventListener("bonus:cart:completed", proceed);

      // Fallback - opens anyway after timeout
      this.__bonusFallbackTimer = setTimeout(() => {
        if (!window.__BONUS_CART_COMPLETED) {
          proceed();
        }
      }, 2500);

      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
      document.dispatchEvent(new CustomEvent("cart:drawer:opened"));
      // Add a global click listener to close the drawer when clicking outside of it.
      // Use a microtask to avoid immediately catching the click that opened the drawer.
      setTimeout(() => {
        this._documentClickHandler = this._onDocumentClick.bind(this);
        document.addEventListener("click", this._documentClickHandler);
      }, 0);
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");

    const flyerForm = document.querySelector("#flyerCodeFormDrawer");
    if (flyerForm) {
      flyerForm.addEventListener("submit", function (e) {
        e.preventDefault();
        const formData = new FormData(e.target);
        const name = formData.get("flyerCode");
        if (!name) return;
        const uppercaseName = String(name).toUpperCase();
        window.location.href =
          window.location.origin +
          "/cart/?utm_campaign=" +
          uppercaseName +
          "&utm_source=cart_field_entry&show_flyer_message=true";
      });
    }

    // Inject app blocks into the drawer on open
    this.injectAppBlocks();
    this.startCtasObserver();
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    // Remove outside click listener if present
    if (this._documentClickHandler) {
      document.removeEventListener("click", this._documentClickHandler);
      this._documentClickHandler = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  fetchLatestSections() {
    const sectionHandles = ["cart-drawer", "cart-icon-bubble"];
    const url = `${window.location.pathname}?sections=${sectionHandles.join(",")}`;
    return fetch(url)
      .then((r) => r.text())
      .then((text) => {
        let sectionsJSON = null;
        try {
          sectionsJSON = JSON.parse(text);
        } catch (e) {
          /* ignore */
        }

        if (sectionsJSON) {
          const updateSection = (handle, selector, targetSelector) => {
            const html = sectionsJSON[handle];
            if (!html) return;
            const tpl = document.createElement("template");
            tpl.innerHTML = html;
            const freshEl = tpl.content.querySelector(selector);
            if (!freshEl) return;
            const target = document.querySelector(targetSelector);
            if (target) target.innerHTML = freshEl.innerHTML;
          };

          updateSection("cart-drawer", "#CartDrawer", "#CartDrawer");
          updateSection(
            "cart-icon-bubble",
            "#cart-icon-bubble",
            "#cart-icon-bubble"
          );
          return;
        }

        // Fallback
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "text/html");
        const freshDrawer = doc.getElementById("CartDrawer");
        if (freshDrawer) {
          const target = document.getElementById("CartDrawer");
          if (target) target.innerHTML = freshDrawer.innerHTML;
        }
        const freshBubble = doc.getElementById("cart-icon-bubble");
        if (freshBubble) {
          const bubble = document.getElementById("cart-icon-bubble");
          if (bubble) bubble.innerHTML = freshBubble.innerHTML;
        }
      });
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    // Preserve any existing app blocks inside the drawer CTAs so they can be restored
    const preservedAppHtml = this.querySelector(".cart__ctas")
      ? this.querySelector(".cart__ctas").innerHTML
      : null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const key = section.section || section.id;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[key],
        section.selector
      );
    });

    // After updating sections via AJAX, ensure app blocks are moved and initialized in the refreshed drawer
    const sectionHtml =
      parsedState.sections["cart-drawer"] || parsedState.sections["CartDrawer"];
    this.injectAppBlocks({ preservedHtml: preservedAppHtml, sectionHtml });
    this.startCtasObserver();

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open(null, { force: true });
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
